import { z } from 'zod';

import { Option } from '../../types/Option';

export const ACportControlMethods = [
    'constant-voltage-frequency',
    'constant-active-reactive-power',
    'uncontrolled',
] as const;

export type ACPortControlMethod = (typeof ACportControlMethods)[number];

export const DCportControlMethods = [
    'constant-voltage',
    'constant-current',
    'constant-power',
    'droop-voltage',
    'power-voltage',
    'maximum-power-point-tracking',
    'uncontrolled',
] as const;

export type DCPortControlMethod = (typeof DCportControlMethods)[number];

export const AllPortControlMethods = [...ACportControlMethods, ...DCportControlMethods] as const;

export type PortControlMethod = (typeof AllPortControlMethods)[number];

export const VoltageControlMethods = [
    'constant-voltage',
    'droop-voltage',
    'power-voltage',
    'constant-voltage-frequency',
    'maximum-power-point-tracking',
] as const satisfies readonly PortControlMethod[];

export type VoltageControlMethod = (typeof VoltageControlMethods)[number];

const options: Option<PortControlMethod>[] = [
    { value: 'constant-voltage', label: 'Constant Voltage' },
    { value: 'constant-current', label: 'Constant Current' },
    { value: 'constant-power', label: 'Constant Power' },
    { value: 'droop-voltage', label: 'Voltage Droop Control' },
    { value: 'power-voltage', label: 'Power-Voltage Droop Control' },
    { value: 'maximum-power-point-tracking', label: 'Maximum Power Point Tracking' },
    { value: 'constant-voltage-frequency', label: 'Constant Voltage Frequency' },
    { value: 'constant-active-reactive-power', label: 'Constant Active and Reactive Power' },
    { value: 'uncontrolled', label: 'Uncontrolled' },
];

const validator = z.array(z.enum(AllPortControlMethods)).default([]);

export const PortControlMethods = {
    validator,
    options,
    abbreviations: {
        'constant-voltage': 'CV',
        'constant-current': 'CC',
        'constant-power': 'CP',
        'droop-voltage': 'DV',
        'power-voltage': 'PV',
        'maximum-power-point-tracking': 'MPPT',
        'uncontrolled': '',
        'constant-voltage-frequency': 'CVF',
        'constant-active-reactive-power': 'CPQ',
    },
    descriptions: {
        'constant-voltage': 'Maintains the bus voltage at a specified setpoint.',
        'constant-current': 'Maintains a specified current through the port.',
        'constant-power': 'Maintains a specified power through the port.',
        'droop-voltage': 'Adjusts voltage based on current to enable load sharing between converters.',
        'power-voltage': 'Adjusts power based on bus voltage to enable power sharing between converters.',
        'maximum-power-point-tracking': 'Maximizes power extracted from a PV source.',
        'constant-voltage-frequency': 'Sets the AC voltage and frequency. Use on only one converter per AC bus.',
        'constant-active-reactive-power': 'Holds a fixed active and reactive power.',
        'uncontrolled':
            'Balances the power across the converter’s ports. Exactly one port must be set to uncontrolled to balance the power.',
    } satisfies Record<PortControlMethod, string>,
};

export type PortControlMethods = z.infer<typeof PortControlMethods.validator>;

export const isACPortControlMethod = (controlMethod: PortControlMethod): controlMethod is ACPortControlMethod => {
    const methods: readonly string[] = ACportControlMethods;
    return methods.includes(controlMethod);
};

export const isDCPortControlMethod = (controlMethod: PortControlMethod): controlMethod is DCPortControlMethod => {
    const methods: readonly string[] = DCportControlMethods;
    return methods.includes(controlMethod);
};

export const isVoltageControlMethod = (controlMethod: PortControlMethod): controlMethod is VoltageControlMethod => {
    const methods: readonly string[] = VoltageControlMethods;
    return methods.includes(controlMethod);
};
